// pages/QrMenuPage.jsx
import { useState, useEffect } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import "../styles/QrMenuPage.css";
import Nav from "./Nav/Nav";
import ScrollToTop from "../components/ScrollToTop";
import MenuSkeleton from "../components/MenuSkeleton/MenuSkeleton";
import { throttle } from "lodash";
import Header from "./Header/Header";

/* ----- Օգնական ֆունկցիա slugify-ի համար ----- */
function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/--+/g, "-");
}

/* ----- MenuSection Subcomponent ----- */
function MenuSection({ section, index, onImageClick }) {
  const sectionId = slugify(section.category);

  return (
    <div
      id={sectionId}
      className="qr-menu-section fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <h3 className="qr-menu-section-title">
        <img src="/icon.png" alt="Default Icon" className="section-icon" />
        {section.category}
        {section.iconUrl && (
          <img
            src={section.iconUrl}
            alt="Category Extra Icon"
            className="section-icon-iconUrl"
            style={{ marginLeft: "4px" }}
          />
        )}
      </h3>
      <ul className="qr-menu-items">
        {section.items?.map((item, idx) => (
          <li key={idx} className="qr-menu-item">
            {item.imageUrl && (
              <img
                src={item.imageUrl}
                alt={item.nameEn || item.nameHy || "Menu item image"}
                className="qr-menu-item-image"
                onClick={() => onImageClick(item.imageUrl)}
                style={{ cursor: "zoom-in" }}
              />
            )}
            <div className="qr-menu-item-text">
              <div className="qr-menu-item-line">
                <span className="item-name-block">
                  {item.nameEn && <span className="item-name-en">{item.nameEn}</span>}
                  {item.nameHy && <span className="item-name-hy">{item.nameHy}</span>}
                </span>
                <span className="dots"></span>
                <strong className="item-price">{item.price} AMD</strong>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ----- Main Component ----- */
export default function QrMenuPage() {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [visibleCount, setVisibleCount] = useState(5); // սկսում ենք 5 item-ով
  const [loadingMore, setLoadingMore] = useState(false);

  const categories = [...new Set(menu.map((s) => s.category))];
  const categorySlugs = categories.map((cat) => ({ name: cat, slug: slugify(cat) }));

  // Fetch menu from Firestore
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const q = query(collection(db, "menu"), orderBy("order"));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setMenu(data);
        localStorage.setItem("menu", JSON.stringify(data));
      } catch (error) {
        console.error("Չհաջողվեց բեռնել մենյուն։", error);
      } finally {
        setLoading(false);
      }
    };

    const cachedMenu = localStorage.getItem("menu");
    if (cachedMenu) {
      setMenu(JSON.parse(cachedMenu));
      setLoading(false);
    }

    fetchMenu();
  }, []);

  // Lazy load on scroll (1 item per scroll)
  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 100;

      if (scrollBottom && visibleCount < menu.length && !loadingMore) {
        setLoadingMore(true); // start skeleton
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + 1, menu.length));
          setLoadingMore(false);
        }, 500); // skeleton animation time
      }
    }, 200);

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [menu, visibleCount, loadingMore]);

  // Smooth appear animation trigger
  useEffect(() => {
    const items = document.querySelectorAll(".qr-menu-section");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.2 }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [menu, visibleCount]);

  // Nav click scroll
  const handleNavClick = (slug) => {
    const scrollToSection = () => {
      const section = document.getElementById(slug);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      } else if (visibleCount < menu.length) {
        setVisibleCount((prev) => Math.min(prev + 1, menu.length));
        setTimeout(scrollToSection, 100);
      }
    };
    scrollToSection();
  };

  if (loading) return <MenuSkeleton />;

  return (
    <div className="qr-menu-page">
        <Header />
      <Nav categories={categorySlugs} onNavClick={handleNavClick} />
      <div className="qr-menu-hero"></div>

      {menu.length === 0 ? (
        <p className="empty-state">
          Մենյուն դեռ դատարկ է։ Խնդրում ենք փորձել ավելի ուշ կամ կապվել մեզ հետ։
        </p>
      ) : (
        <>
          {menu.slice(0, visibleCount).map((section, index) => (
            <div key={section.id}>
              <MenuSection section={section} index={index} onImageClick={setPreviewImage} />
              {index === 0 && (
                <div className="after-first-category-text">
                  <img className="bg2" src="/bg2.png" alt="Coffee Icon" />
                  <p>
                    <img
                      className="mlg"
                      src="/malongo-logo.svg"
                      alt="Malongo logo"
                      width={10}
                      height={40}
                    />
                    is a French company specializing in high-quality coffee, known for its
                    commitment to fair trade and organic practices, as well as its
                    innovation in coffee packaging and brewing systems.
                  </p>
                </div>
              )}
            </div>
          ))}

          {loadingMore && <MenuSkeleton />}
        </>
      )}

      {previewImage && (
        <div className="image-modal" onClick={() => setPreviewImage(null)}>
          <img
            src={previewImage}
            alt="Preview"
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          />
          <button className="image-modal-close" onClick={() => setPreviewImage(null)}>
            ×
          </button>
        </div>
      )}

      <ScrollToTop />
    </div>
  );
}
